import { axiosInstanceWhatsAppConfirmation } from "../instance";

// ---- Types ----

export interface WhatsAppAccount {
  id: number;
  owner_id: string;
  phone_number_id: string;
  waba_id: string;
  display_name: string | null;
  is_active: boolean;
}

export interface CreateWhatsAppAccountRequest {
  owner_id: string;
  phone_number_id: string;
  waba_id: string;
  access_token: string;
  app_secret?: string;
  verify_token: string;
  display_name?: string;
}

export interface Confirmation {
  id: number;
  order_id: number;
  order_hash_id: string;
  customer_name: string | null;
  customer_phone: string;
  status: ConfirmationStatus;
  retry_count: number;
  created_at: string | null;
  confirmed_at: string | null;
}

export type ConfirmationStatus =
  | "pending"
  | "awaiting_response"
  | "confirmed"
  | "rejected"
  | "expired"
  | "canceled"
  | "send_failed";

export interface ConversationMessage {
  role: "assistant" | "user";
  content: string;
  timestamp: string;
  action?: string | null;
  message_type?: "template" | "text";
  status?: "sending" | "sent" | "delivered" | "read" | "failed";
}

export interface Conversation {
  id: number;
  customer_phone: string;
  messages: ConversationMessage[];
  is_active: boolean;
  ai_enabled: boolean;
  started_at: string | null;
  ended_at: string | null;
}

export interface ReportsSummary {
  owner_id: string;
  total: number;
  by_status: Record<string, number>;
  confirmation_rate: number;
}

// ---- API functions ----

export const getWhatsAppAccount = async (
  ownerId: string
): Promise<WhatsAppAccount> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/whatsapp-accounts/${ownerId}`
  );
  return response.data;
};

export const createWhatsAppAccount = async (
  data: CreateWhatsAppAccountRequest
) => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/whatsapp-accounts",
    data
  );
  return response.data;
};

export const getConfirmations = async (
  ownerId: string,
  status?: ConfirmationStatus,
  limit: number = 50,
  offset: number = 0
): Promise<Confirmation[]> => {
  const params: Record<string, string | number> = {
    owner_id: ownerId,
    limit,
    offset,
  };
  if (status) params.status = status;
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/confirmations",
    { params }
  );
  return response.data;
};

export const getConversation = async (
  confirmationId: number
): Promise<Conversation> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    `/admin/confirmations/${confirmationId}/conversation`
  );
  return response.data;
};

export const getReportsSummary = async (
  ownerId: string
): Promise<ReportsSummary> => {
  const response = await axiosInstanceWhatsAppConfirmation.get(
    "/admin/reports/summary",
    { params: { owner_id: ownerId } }
  );
  return response.data;
};

export const resendConfirmation = async (
  confirmationId: number
): Promise<{ status: string; order_id: number }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/confirmations/${confirmationId}/resend`
  );
  return response.data;
};

// ---- AI Toggle & Manual Messaging ----

export const toggleAIMode = async (
  confirmationId: number,
  aiEnabled: boolean
): Promise<{ confirmation_id: number; ai_enabled: boolean }> => {
  const response = await axiosInstanceWhatsAppConfirmation.patch(
    `/admin/conversations/${confirmationId}/ai-toggle`,
    { ai_enabled: aiEnabled }
  );
  return response.data;
};

export const sendManualMessage = async (
  confirmationId: number,
  message: string
): Promise<{ status: string; messages: ConversationMessage[] }> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    `/admin/conversations/${confirmationId}/send-message`,
    { message }
  );
  return response.data;
};

// ---- OAuth Embedded Signup ----

export interface WhatsAppOAuthCallbackRequest {
  code: string;
  phone_number_id: string;
  waba_id: string;
  owner_id: string;
}

export interface WhatsAppOAuthCallbackResponse {
  status: string;
  owner_id: string;
  phone_number_id: string;
  waba_id: string;
  verify_token: string;
}

export const whatsappOAuthCallback = async (
  data: WhatsAppOAuthCallbackRequest
): Promise<WhatsAppOAuthCallbackResponse> => {
  const response = await axiosInstanceWhatsAppConfirmation.post(
    "/admin/whatsapp-accounts/oauth/callback",
    data
  );
  return response.data;
};
